import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';

type ThemeContextValue = {
  theme: Theme;
  effective: 'light' | 'dark';
  setTheme: (t: Theme) => void;
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

function getSystemPref(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export const ThemeProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const raw = localStorage.getItem('theme');
      return (raw as Theme) || 'system';
    } catch {
      return 'system';
    }
  });

  const [effective, setEffective] = useState<'light' | 'dark'>(() => (theme === 'system' ? getSystemPref() : theme));

  useEffect(() => {
    const apply = (t: Theme) => {
      const eff = t === 'system' ? getSystemPref() : t;
      setEffective(eff);
      const root = document.documentElement;
      if (eff === 'dark') root.classList.add('dark');
      else root.classList.remove('dark');
    };
    apply(theme);

    const mql = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
    const listener = () => {
      if (theme === 'system') apply('system');
    };

    if (mql && typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', listener);
    }

    return () => {
      if (mql && typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', listener);
      }
    };
  }, [theme]);

  const setTheme = (t: Theme) => {
    try {
      localStorage.setItem('theme', t);
    } catch (err) {
      // ignore write errors (e.g., storage disabled) but log for debugging
      // eslint-disable-next-line no-console
      console.warn('Failed to persist theme to localStorage', err);
    }
    setThemeState(t);
  };

  const toggle = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  return <ThemeContext.Provider value={{ theme, effective, setTheme, toggle }}>{children}</ThemeContext.Provider>;
};

export default ThemeProvider;
